import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router';
import { GET_CLUB_BY_EMAIL } from "../graphql/clubModel/ClubQuery";
import { AuthContext } from "./AuthContext";
import LoadingIndicator from "../components/LoadingIndicator";
export const ClubDetailsContext = createContext({});

const AVAILABILITY_NOTIFICATION_TYPES = new Set([
    'VENUE_SUSPENDED',
    'VENUE_SUSPENSION_CLEARED',
]);

export const ClubDetailsProvider = ({ children }) => {
    const { club, clubDetails, setClubDetails } = useContext(AuthContext);
    const location = useLocation();
    const previousPath = useRef(location.pathname);
    const { loading, error, data, refetch } = useQuery(GET_CLUB_BY_EMAIL, {
        variables: {
            email: club?.email,
        },
        skip: !club?.email,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (club && data?.getClubByEmail?.email) {
            setClubDetails(data.getClubByEmail);
        }
    }, [data, club, setClubDetails]);

    useEffect(() => {
        if (!club?.email || typeof refetch !== 'function') return undefined;

        let refreshInFlight = false;
        const refreshClubDetails = () => {
            if (refreshInFlight) return;
            refreshInFlight = true;
            Promise.resolve(refetch())
                .catch(() => {
                    // The existing club details remain usable if a background refresh fails.
                })
                .finally(() => {
                    refreshInFlight = false;
                });
        };
        const refreshWhenVisible = () => {
            if (document.visibilityState === 'visible') refreshClubDetails();
        };
        const refreshForAvailabilityNotification = event => {
            const notification = event.detail || {};
            const notificationData = notification.data || notification;
            if (!AVAILABILITY_NOTIFICATION_TYPES.has(notificationData.type)) return;
            if (
                notificationData.clubId &&
                clubDetails?.id &&
                String(notificationData.clubId) !== String(clubDetails.id)
            ) return;
            refreshClubDetails();
        };

        window.addEventListener('focus', refreshClubDetails);
        document.addEventListener('visibilitychange', refreshWhenVisible);
        window.addEventListener('boozeon:notification', refreshForAvailabilityNotification);

        return () => {
            window.removeEventListener('focus', refreshClubDetails);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
            window.removeEventListener('boozeon:notification', refreshForAvailabilityNotification);
        };
    }, [club?.email, clubDetails?.id, refetch]);

    useEffect(() => {
        const pathChanged = previousPath.current !== location.pathname;
        previousPath.current = location.pathname;
        if (!pathChanged || !club?.email || typeof refetch !== 'function') return;

        // Route changes are a reliable fallback when a push notification is
        // unavailable or was missed while the venue portal remained open.
        refetch().catch(() => {
            // Keep the current page and cached venue details usable on failure.
        });
    }, [club?.email, location.pathname, refetch]);


    if (loading && !clubDetails) {
        return <LoadingIndicator />
    }

    if (error) {
        alert('Error in fetching Club Profile Data')
    }

    return (
        <ClubDetailsContext.Provider
            value={{
                clubDetails,
                setClubDetails,
            }}>
            {children}
        </ClubDetailsContext.Provider>
    );
};
